import { Clock, CheckCircle2, Truck, PackageCheck, XCircle } from 'lucide-react';

const STAGES = [
  { key: 'pending', label: 'Pendiente', icon: Clock, rank: 0 },
  { key: 'approved', label: 'Aprobado', icon: CheckCircle2, rank: 1 },
  { key: 'in_transit', label: 'En camino', icon: Truck, rank: 2 },
  { key: 'delivered', label: 'Entregado', icon: PackageCheck, rank: 3 },
];

// Map every overall order status to one of the four buyer-facing stages.
const RANK = {
  pending_approval: 0,
  approved: 1,
  preparing: 1,
  ready_for_delivery: 1,
  in_transit: 2,
  partial_delivery: 2,
  delivered: 3,
};

const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

export const OrderTimeline = ({ order, id }) => {
  const history = Array.isArray(order?.history) ? order.history : [];
  const cancelled = order?.status === 'cancelled';
  const knownRanks = history.map((h) => RANK[h.status]).filter((r) => r != null);
  const currentRank = RANK[order?.status] != null ? RANK[order.status] : (knownRanks.length ? Math.max(...knownRanks) : 0);
  const reachedRank = Math.max(currentRank, ...(knownRanks.length ? knownRanks : [0]));

  const stampFor = (rank) => {
    const ats = history.filter((h) => RANK[h.status] != null && RANK[h.status] >= rank && h.at).map((h) => h.at).sort();
    return ats.length ? ats[0] : '';
  };

  if (cancelled) {
    const cancelledAt = history.filter((h) => h.status === 'cancelled' && h.at).map((h) => h.at).sort().pop();
    return (
      <div className="mt-4 rounded-lg border border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30 p-3 flex items-center gap-3" data-testid={id ? `${id}-cancelled` : undefined}>
        <XCircle className="h-5 w-5 text-red-600 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-red-700 dark:text-red-300">Pedido cancelado</p>
          {cancelledAt && <p className="text-xs text-red-600/80 dark:text-red-400/80">{fmt(cancelledAt)}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4" data-testid={id} role="list" aria-label="Estado de entrega del pedido">
      <div className="flex items-start">
        {STAGES.map((stage, i) => {
          const done = stage.rank <= reachedRank;
          const current = stage.rank === currentRank;
          const Icon = stage.icon;
          const stamp = done ? stampFor(stage.rank) : '';
          return (
            <div key={stage.key} className="flex-1 flex flex-col items-center relative" role="listitem" data-testid={id ? `${id}-step-${stage.key}` : undefined} aria-current={current ? 'step' : undefined}>
              {i > 0 && (
                <span
                  aria-hidden="true"
                  className={`absolute top-4 right-1/2 h-0.5 w-full -z-0 ${stage.rank <= reachedRank ? 'bg-brand-orange' : 'bg-border'}`}
                />
              )}
              <span
                className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors duration-200 ${
                  done ? 'border-brand-orange bg-brand-orange text-white' : 'border-border bg-card text-muted-foreground'
                } ${current ? 'ring-2 ring-brand-orange/30' : ''}`}
                data-testid={id ? `${id}-dot-${stage.key}${done ? '-done' : ''}` : undefined}
              >
                <Icon className="h-4 w-4" />
              </span>
              <span className={`mt-2 text-center text-xs font-medium ${done ? 'text-foreground' : 'text-muted-foreground'}`}>{stage.label}</span>
              {stamp && <span className="mt-0.5 text-center text-[10px] text-muted-foreground leading-tight">{fmt(stamp)}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderTimeline;
